import { ColDef } from 'ag-grid-community';

import Typography from '~/shared/components/Typography';
import Exception from '~/shared/assets/icons/exception-2.svg';
import SizeCell from '~/shared/components/SizeCell';

import { AutoscaleRule } from './hooks/useAutoscaleRulesQuery';
import ConditionsRenderer from './ConditionsRenderer';
import ToggleRuleRenderer from './ToggleRuleRenderer';

export function getAutoscaleRulesColumnDefs(
  accountName?: string,
  isUserReadOnly?: boolean,
  windowWidth?: number
): ColDef[] {
  const isSmallScreen = (windowWidth || 1600) <= 1440;

  return [
    {
      field: 'ruleId',
      headerName: 'Rule ID',
      width: 200,
      minWidth: 200,
      suppressSizeToFit: true,
      flex: 1,
      sortable: false,
      cellRenderer: ({ value }: { value: string }) => (
        <Typography
          title={value}
          variant="p3-reg"
          color="text-blue-600"
          className={`font-medium text-[14px] overflow-hidden whitespace-nowrap ${value?.length > 16 ? 'text-ellipsis' : ''}`}
        >
          {value || '-'}
        </Typography>
      ),
    },
    {
      field: 'conditions',
      headerName: 'Conditions',
      width: isSmallScreen ? 488 : 868,
      flex: 3,
      minWidth: 488,
      resizable: true,
      sortable: false,
      cellRenderer: ({ data }: { data: AutoscaleRule }) => <ConditionsRenderer data={data} />,
      valueFormatter: () => '', // Suppress AG Grid warning for object data type
    },
    {
      field: 'volumes',
      headerName: 'Volumes',
      width: 100,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value }: { value: number }) => (
        <Typography variant="p3-reg" color="text-gray-900" fontWeight={500} className="text-[14px]">
          {value?.toLocaleString() || '-'}
        </Typography>
      ),
    },
    {
      field: 'capacity',
      headerName: 'Capacity',
      width: 120,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value }: { value: number }) => {
        const size = value || 0;

        return size !== undefined && size !== null ? <SizeCell size={size} /> : 'N/A';
      },
    },
    {
      field: 'utilization',
      headerName: 'Utilization',
      width: 120,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value, data }: { value: string; data: AutoscaleRule }) => {
        const showNA = !value || data.volumes === 0;
        const displayValue = showNA ? 'N/A' : value;

        return (
          <div className="flex items-center ">
            <Typography variant="p3-reg" color="text-gray-900" className="min-w-[3rem] text-[14px]">
              {displayValue}
            </Typography>
          </div>
        );
      },
    },
    {
      field: 'exceptions',
      headerName: 'Exceptions',
      width: 120,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: ({ value }: { value: number }) => {
        return (
          <div className="flex items-center gap-1.5">
            {value === 0 ? (
              <div className="size-5 rounded-full bg-transparent" />
            ) : (
              <Exception className="size-5 text-gray-600 " />
            )}
            <Typography
              variant="p3-reg"
              fontWeight={500}
              color="text-gray-900"
              className="text-[14px]"
            >
              {value}
            </Typography>
          </div>
        );
      },
    },
    // {
    //   field: 'account',
    //   headerName: 'Account',
    //   flex: 0.5,
    //   minWidth: 120,
    //   cellRenderer: () => {
    //     return (
    //       <div className="flex gap-2 items-center">
    //         <Typography variant="p3" fontWeight={400} className="text-primary-900">
    //           {accountName || '-'}
    //         </Typography>
    //       </div>
    //     );
    //   },
    // },
    {
      field: 'toggle',
      headerName: '',
      width: 104,
      suppressSizeToFit: true,
      suppressNavigable: true, // Prevent cell from being navigable
      cellRenderer: ({ data }: { data: AutoscaleRule }) => {
        return <ToggleRuleRenderer data={data} isUserReadOnly={isUserReadOnly} />;
      },
    },
  ];
}
